/**
 * A printed QR, resolved.
 *
 * Labels carry APP.CARDCHASE.ORG/C/TS2SWJ rather than a URL with two UUIDs in
 * it, because a QR's readability is the physical size of one module and a 12mm
 * label only has room for about 25 of them across. The long link needed 41 and
 * would not scan at any size that fits on the strip.
 *
 * This screen is a doorway and nothing else: it looks the code up and sends the
 * person to the card they were always going to. No new public screen, no second
 * thing to keep in step with card detail - the guest already gets the full
 * pricing there, and can page through the binder and back out to it.
 *
 * The binder comes free. A card has exactly one `binder_id`, so the code names
 * the card and the binder follows; the old ?b= parameter is gone.
 */

import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function ShortCardLink({ fallback = null }) {
  const { code } = useParams();
  const [state, setState] = useState({ status: 'looking' });

  useEffect(() => {
    let alive = true;
    (async () => {
      // Printed uppercase for QR's alphanumeric mode, which is nearly twice as
      // dense as mixed case. Typed by a human it could be anything.
      const wanted = String(code || '').trim().toUpperCase();
      if (!wanted) { setState({ status: 'missing' }); return; }

      const { data, error } = await supabase
        .from('cards')
        .select('id, binder_id')
        .eq('short_code', wanted)
        .is('deleted_at', null)
        .maybeSingle();

      if (!alive) return;
      // A code that resolves to nothing is the same to the reader whether the
      // row is gone or RLS hid it, and saying which would leak the difference.
      if (error || !data) { setState({ status: 'missing' }); return; }
      setState({ status: 'found', card: data });
    })();
    return () => { alive = false; };
  }, [code]);

  if (state.status === 'looking') return fallback;
  if (state.status === 'missing') return <Navigate to="/" replace />;

  const { id, binder_id: binderId } = state.card;
  return <Navigate to={`/card/${id}${binderId ? `?b=${binderId}` : ''}`} replace />;
}
